import React, { useRef } from 'react';
import { BrochurePagination } from '$partials/global';

const renderedModules = [
    'ImageCarouselBlock',
    'TwoImagesBlock',
    'TwoImagesLandscapeBlock',
    'ThreeImagesBlock',
    'FourImagesBlock'
];

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/partials/modules.d.ts').BrochureSection}
 */
const BrochureSection = ({
    title,
    body,
    standfirst,
    modules,
    propertyName,
}) => {
    const pageRef = useRef(null);

    const remainingModules = modules?.filter(module => module.__typename !== 'LargeImageBlock' && module.__typename !== 'ImageBlock' );

    const isPortrait = (img1, img2) => {
        const width = (img1?.width || 0) + (img2?.width || 0);
        const height = (img1?.height || 0) + (img2?.height || 0);

        return height > width;
    };

    const renderModule = (module, shouldRenderText) => {
        if (!renderedModules.includes(module.__typename)) {
            return null;
        }
        let cover1 = null;
        let cover2 = null;

        if ( module.__typename === 'ImageCarouselBlock') {
            // check if any item has toggle turned on
            const isManualSelection = module.items.some(item => item.isBrochureCover === true);
            if (isManualSelection) {
                const covers = module.items.filter(item => item.isBrochureCover === true);
                cover1 = covers[0];
                cover2 = covers[1];
            } else {
                cover1 = module.items[0];
                cover2 = module.items[1];
            }
        }

        if (module.__typename === 'ThreeImagesBlock' || module.__typename === 'FourImagesBlock') {
            // check if any item has toggle turned on
            const isManualSelection = Object.entries(module).some(([key, value]) => key.startsWith('isBrochureCover') && value === true);

            if (isManualSelection) {
                const brochureKeys = Object
                    .entries(module)
                    .filter(([key, value]) => key.startsWith('isBrochureCover') && value === true);

                const covers = brochureKeys.map(([k]) => {
                    const imageNumber = k.split('isBrochureCover')[1];
                    const image = module[`image${imageNumber}`];
                    const caption = module[`caption${imageNumber}`];
                    return { brochureImage: image, caption };
                });

                cover1 = covers[0];
                cover2 = covers[1];
            }
        }

        const image1 = cover1?.brochureImage || module.brochureImage1;
        const image2 = cover2?.brochureImage || module.brochureImage2;
        const caption1 = cover1?.caption || module.caption1;
        const caption2 =  cover2?.caption || module.caption2;

        if (isPortrait(image1, image2) || module.__typename === 'ThreeImagesBlock' || module.__typename === 'FourImagesBlock') {
            return (
                <div className='width-100 flex flex-wrap'>
                    <div className='print-col-8 flex justify-end mt9'>
                        <div className='print-col-3'>
                            <h3 className='print-type-27px'>
                                {shouldRenderText && title}
                            </h3>
                        </div>
                    </div>
                    <div className='print-col-8 flex mt7'>
                        <div className='print-col-5'>
                            <div
                                className='flex width-100'
                                style={{transform: 'translateX(-60px)'}}>
                                <div className='mr5 col-6'>
                                    <img
                                        className='height-100 width-100 object-cover'
                                        src={image1?.src} />
                                </div>
                                {image2 && (
                                    <div className='col-6'>
                                        <img
                                            className='height-100 width-100 object-cover'
                                            src={image2.src} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='print-col-3 flex flex-col'>
                            {shouldRenderText && standfirst && (
                                <div
                                    className='rich-text print-type-12px mb3'
                                    dangerouslySetInnerHTML={{__html: standfirst}} />
                            )}
                            {shouldRenderText && (
                                <div
                                    className='rich-text print-type-12px mb6'
                                    dangerouslySetInnerHTML={{__html: body}} />
                            )}

                            {(caption1 || caption2) && (
                                <div className='border-top mb4'>
                                    {caption1 && (
                                        <div className='flex pt3'>
                                            <div className='col-4'>
                                                <p className='print-type-9px'>
                                                    img 1
                                                </p>
                                            </div>
                                            <figcaption className='col-8'>
                                                <div
                                                    className='print-type-9px'
                                                    dangerouslySetInnerHTML={{__html: caption1}} />
                                            </figcaption>
                                        </div>
                                    )}
                                    {caption2 && (
                                        <div className='flex pt3'>
                                            <div className={`col-4${caption2 ? ' pt3' : ''}`}>
                                                <p className='print-type-9px'>
                                                    img 2
                                                </p>
                                            </div>
                                            <figcaption className={`col-8${caption1 ? ' border-top pt3' : ''}`}>
                                                <div
                                                    className='print-type-9px'
                                                    dangerouslySetInnerHTML={{__html: caption2}} />
                                            </figcaption>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='print-col-8 flex height-100'>
                    <div className='print-col-5 height-100 z1'>
                        <div
                            className='width-100 height-100 flex flex-col pl6'
                            style={{transform: 'translateX(-60px)'}}>
                            <figure
                                className='width-100 mb6'
                                style={{height: 'calc((100% - 30px) / 2)'}}>
                                {image1 && (
                                    <img
                                        className='height-100 width-100 object-cover'
                                        src={image1?.src} />
                                )}
                            </figure>
                            <figure
                                className='width-100'
                                style={{height: 'calc((100% - 30px) / 2)'}}>
                                {image2 && (
                                    <img
                                        className='height-100 width-100 object-cover'
                                        src={image2?.src} />
                                )}
                            </figure>
                        </div>
                    </div>
                    <div className='print-col-3 flex flex-col'>
                        <h3 className='print-type-27px mt9 mb7'>
                            {shouldRenderText && title}
                        </h3>
                        {standfirst && (
                            <div
                                className='rich-text print-type-12px mb3'
                                dangerouslySetInnerHTML={{__html: standfirst}} />)}
                        <div
                            className='rich-text print-type-12px mb6'
                            dangerouslySetInnerHTML={{__html: body}} />
                        {(caption1 || caption2) && (
                            <div className='border-top mb4 mt9'>
                                {caption1 && (
                                    <div className='flex pt3'>
                                        <div className='col-4'>
                                            <p className='print-type-9px'>
                                                img 1
                                            </p>
                                        </div>
                                        <figcaption className='col-8'>
                                            {shouldRenderText && (
                                                <div
                                                    className='print-type-9px'
                                                    dangerouslySetInnerHTML={{__html: caption1}}/>
                                            )}
                                        </figcaption>
                                    </div>
                                )}
                                {caption2 && (
                                    <div className='flex pt3'>
                                        <div className={`col-4${caption1 ? ' pt3' : ''}`}>
                                            <p className='print-type-9px'>
                                                img 2
                                            </p>
                                        </div>
                                        <figcaption className={`col-8${caption1 ? ' border-top pt3' : ''}`}>
                                            <div
                                                className='print-type-9px'
                                                dangerouslySetInnerHTML={{__html: caption2}} />
                                        </figcaption>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
    };

    return (
        <>
            {modules?.length > 0 && modules.map((module, idx) => {
                if ((module.__typename === 'LargeImageBlock' || module.__typename === 'ImageBlock') && !remainingModules?.length) {
                    return (
                        <div
                            className='print-page bg-white px8'
                            key={module.image.id}
                            ref={pageRef}>
                            <div className='print-col-8 flex height-100'>
                                <div className='print-col-5 height-100 z1'>
                                    <div
                                        className='width-100 height-100 flex flex-col mt9 pl6'
                                        style={{transform: 'translateX(-60px)'}}>
                                        <figure
                                            className='width-100 mb6'
                                            style={{height: 'calc((100% - 30px) / 2)'}}>
                                            <img
                                                className='height-100 width-100 object-cover'
                                                src={module.image.src} />
                                        </figure>
                                    </div>
                                </div>
                                <div className='print-col-3 flex flex-col'>
                                    <h3 className='print-type-27px mt9 mb7'>
                                        {title}
                                    </h3>
                                    {standfirst && (<div
                                        className='rich-text print-type-12px mb3'
                                        dangerouslySetInnerHTML={{__html: standfirst}} />)}
                                    <div
                                        className='rich-text print-type-12px mb6'
                                        dangerouslySetInnerHTML={{__html: body}} />
                                </div>
                            </div>
                            <BrochurePagination
                                propertyName={propertyName}
                                ref={pageRef} />
                        </div>
                    );
                }
                else if (module.__typename === 'LargeImageBlock' || module.__typename === 'ImageBlock') {
                    return (
                        <div
                            className='print-page bg-white'
                            key={module.image.id}>
                            <figure className='height-100 width-100'>
                                <img
                                    className='height-100 width-100 object-cover'
                                    src={module.image.src} />
                            </figure>
                        </div>
                    );
                }
                else if (module.__typename === 'ImageCarouselBlock' || module.__typename === 'TwoImagesBlock' || module.__typename === 'TwoImagesLandscapeBlock'  || module.__typename === 'ThreeImagesBlock' || module.__typename === 'FourImagesBlock') {
                    const shouldRenderText = !(!modules.find(module => module.__typename ==='LargeImageBlock' || module.__typename === 'ImageBlock') && idx === 1);
                    return (
                        <div
                            className='print-page bg-white px8'
                            key={`brochure-image-block-${idx}`}
                            ref={pageRef}>
                            {renderModule(module, shouldRenderText)}
                            <BrochurePagination
                                propertyName={propertyName}
                                ref={pageRef} />
                        </div>
                    );
                }
            })}
            {!modules?.length && (
                <div
                    className='print-page bg-white px8'
                    ref={pageRef}>
                    <div className='width-100 flex flex-wrap'>
                        <div className='print-col-8 flex justify-end mt9'>
                            <div className='print-col-3'>
                                <h3 className='print-type-27px mb7'>
                                    {title}
                                </h3>
                                {standfirst && (<div
                                    className='rich-text print-type-12px mb3'
                                    dangerouslySetInnerHTML={{__html: standfirst}} />)}
                                <div
                                    className='rich-text print-type-12px mb6'
                                    dangerouslySetInnerHTML={{__html: body}} />
                            </div>
                        </div>
                    </div>
                    <BrochurePagination
                        propertyName={propertyName}
                        ref={pageRef} />
                </div>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default BrochureSection;
